import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { NzModalService } from 'ng-zorro-antd/modal';
import { Observable, tap } from 'rxjs';
import { SharedService } from '@shared/service/shared.service';

/**
 * 临时登录被后端拦截时拉起安全验证弹窗（bug 18729）
 *
 * 敏感接口在「临时登录」状态下会被后端以 403 + detail「当前为临时登录状态，请先完成安全验证后再操作」拒绝，
 * 只弹文字提示会让用户操作中断、还要另找入口验证，这里统一拉起「重要安全提醒」弹窗，用户可当场扫码/绑定手机验证。
 * 用拦截器而非 ErrorHandler：组件内 catch 掉的错误到不了全局 ErrorHandler，拦截器才能覆盖所有接口。
 */
@Injectable()
export class TrustedLoginInterceptor implements HttpInterceptor {
  private modalSrv = inject(NzModalService);
  private sharedSrv = inject(SharedService);

  /** 弹窗打开期间去重：并发请求会同时收到 403，避免叠出多个弹窗 */
  private safeModalOpen = false;

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      tap({
        error: (error: unknown) => {
          if (this.isTrustedLoginError(error)) this.openSafeModal();
        },
      })
    );
  }

  /** 后端 403 且 detail 指明临时登录/安全验证，即操作被安全验证拦截 */
  private isTrustedLoginError(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse) || error.status !== 403) return false;
    const detail = typeof error.error?.detail === 'string' ? error.error.detail : '';
    return detail.includes('临时登录') || detail.includes('安全验证');
  }

  /** 与 CheckLoginTypeDirective.showSafeModal 保持同一弹窗配置 */
  private async openSafeModal() {
    if (this.safeModalOpen) return;
    this.safeModalOpen = true;

    // 动态引入弹窗组件：app.module 静态依赖它会改变 shared 模块的初始化顺序，
    // dev 构建下导致 exam 模块组件的管道解析成 undefined（ɵcmp undefined 白屏）
    const { LinkAccountComponent } = await import('@shared/component/link-account/link-account.component');

    const modal = this.modalSrv.create({
      nzTitle: '重要安全提醒',
      nzContent: LinkAccountComponent,
      nzWidth: '750px',
      nzClosable: false,
      nzData: {
        trustedLoginStatus: this.sharedSrv.user?.trustedLoginStatus,
        credibleWechat: this.sharedSrv.user?.credibleWechat,
      },
      nzCancelText: null,
      nzOkText: '暂不验证',
      nzMaskClosable: false,
    });
    modal.afterClose.subscribe(() => {
      this.safeModalOpen = false;
    });
  }
}
